import { FC, useEffect, useMemo, useState } from 'react';

import { isEqual } from 'lodash';

import { useDispatch, useSelector } from 'react-redux';
import { changeDealData, hidePlannerSecondaryPanel } from 'store/dealManagement/reducer';

import PlannerSecondaryPanel from 'components/pages/Planner/PlannerSecondaryPanel';
import { useCategoryOptions } from 'customHooks/useCategoryOptions';
import { canDisplayOptionResults } from 'components/pages/Planner/PlannerSecondaryPanel/utils';
import { shouldDisplayCategoryClearAll } from 'components/pages/Planner/PlannerSections/FiltersContent/utils';

import CollapsibleList from 'lib/CollapsibleList';
import CollapsibleListStatsHeader from 'lib/CollapsibleList/CollapsibleListStatsHeader';
import SelectOption from 'lib/SelectOption';

import { CodeNameModel } from 'components/common/types';
import { Store } from 'components/common/types/Store.types';
import { TruncatedElementWithTooltip } from 'components/common/TruncatedElementWithTooltip/TruncatedElementWithTooltip';
import Search from 'lib/Search';
import { DirectSalesCodeNameModel } from 'components/common/types/DirectSalesCampaign.types';

const JuxtapositionPanel: FC = () => {
  const dispatch = useDispatch();

  const storedProductCategories = useSelector(
    (state: Store) => state.dealManagement.commonDeal.exclusions.productCategories,
  );
  const organisations = useSelector((state: Store) => state.dealManagement.commonDeal.exclusions.organisations);
  const productCategoryGroups = useSelector((state: Store) => state.dealManagement.meta.productCategoryGroups) || [];

  const [tmpProductCategories, setTmpProductCategories] = useState<DirectSalesCodeNameModel[]>(storedProductCategories);

  const productCategoriesGroupMap = useMemo(() => {
    return productCategoryGroups.reduce(
      (acc, cur) => {
        acc[cur.code] = cur.productCategories ?? [];
        return acc;
      },
      {} as Record<string, CodeNameModel[]>,
    );
  }, [productCategoryGroups]);

  const {
    searchText,
    selectedOptions: selectedCampaignProductFormats,
    onCategoryDirectionSort,
    openCategories,
    setSelectedOptions,
    filteredOptions,
    getNumberOfFilteredOptions,
    sortDirection,
    isOptionSelected,
    onFilterTextChange,
    onToggleCategory,
    onOptionIncludeExcludeChange,
  } = useCategoryOptions({
    options: productCategoriesGroupMap,
    initialSelectedOptions: tmpProductCategories,
  });

  const categoryNameMap = useMemo(() => {
    return Object.keys(productCategoriesGroupMap).reduce(
      (acc, categoryId) => {
        productCategoriesGroupMap[categoryId].forEach((category) => {
          acc[category.code] = categoryId;
        });
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [productCategoriesGroupMap]);

  const selectedOptionsWithGroupName = useMemo(() => {
    return selectedCampaignProductFormats.map((selected) => ({
      ...selected,
      category: categoryNameMap[selected.code],
    }));
  }, [selectedCampaignProductFormats, categoryNameMap]);

  const productCategoryLabels = useMemo(() => {
    return Object.keys(productCategoriesGroupMap).reduce(
      (acc, cur) => {
        acc[cur] = productCategoryGroups.find((group) => group.code === cur)?.name || '';
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [productCategoriesGroupMap]);

  const isChange = useMemo(
    () => !isEqual(storedProductCategories, selectedCampaignProductFormats),
    [storedProductCategories, selectedCampaignProductFormats],
  );

  const onSubmit = (): void => {
    dispatch(changeDealData({ exclusions: { organisations, productCategories: selectedOptionsWithGroupName } }));
    dispatch(hidePlannerSecondaryPanel());
  };

  useEffect(() => {
    setTmpProductCategories(selectedOptionsWithGroupName);
  }, [isChange]);

  useEffect(() => {
    setTmpProductCategories(storedProductCategories);
  }, [storedProductCategories]);

  return (
    <PlannerSecondaryPanel
      size="lg"
      title="Add product category"
      confirmButtonLabel="Add"
      cancelButtonLabel="Back"
      showBackButton
      customStyles={{ left: '430px' }}
      isLineLevel={false}
      isChange={isChange}
      onSubmit={onSubmit}
      onCancel={() => dispatch(hidePlannerSecondaryPanel())}
    >
      <div className="mb-2">
        <Search value={searchText} placeholder="Search product category" onChange={onFilterTextChange} />
      </div>

      {canDisplayOptionResults(filteredOptions) ? (
        !!filteredOptions &&
        Object.keys(filteredOptions).map((category: keyof typeof productCategoryLabels) => {
          const selectedOptions = selectedCampaignProductFormats.filter(
            (dealLineFormat) => dealLineFormat.category !== productCategoryLabels[category],
          );

          return (
            <CollapsibleList
              label={
                <TruncatedElementWithTooltip tooltip={productCategoryLabels[category]}>
                  {(ref) => (
                    <span ref={ref} className="block max-w-[200px] truncate">
                      {productCategoryLabels[category]}
                    </span>
                  )}
                </TruncatedElementWithTooltip>
              }
              isSortVisible
              onSort={onCategoryDirectionSort}
              sortDir={sortDirection}
              isOpen={openCategories.includes(category)}
              category={category}
              onToggle={onToggleCategory}
              isClearAllVisible={shouldDisplayCategoryClearAll(
                selectedCampaignProductFormats,
                productCategoryLabels[category],
              )}
              onClearAll={() => setSelectedOptions(selectedOptions)}
              key={`product-group-${productCategoryLabels[category]}`}
              headerComponent={
                <CollapsibleListStatsHeader
                  available={filteredOptions[category].length}
                  excluded={getNumberOfFilteredOptions(category, selectedCampaignProductFormats)}
                />
              }
            >
              {filteredOptions[category].map((subOption) => {
                return (
                  <SelectOption
                    value={{
                      code: subOption.code,
                      name: subOption.name,
                    }}
                    key={`select-category-${subOption.code}`}
                    isSelected={isOptionSelected(subOption.code, category)}
                    isDisabled={false}
                    isMultiple
                    hasCheckbox
                    isIncludeExcludeAlwaysVisible
                    classNames="!m-0"
                    onClick={(value: CodeNameModel) =>
                      onOptionIncludeExcludeChange({
                        ...value,
                        category,
                      })
                    }
                  >
                    {subOption.name}
                  </SelectOption>
                );
              })}
            </CollapsibleList>
          );
        })
      ) : (
        <p>There are no results matching your search</p>
      )}
    </PlannerSecondaryPanel>
  );
};

export default JuxtapositionPanel;
